// @figma file=pkCGby54zXzTsccXvHqzO6 node=408:8904 name="Tabs / Tab item" set="mytaible-shadcn-studio" verified=2026-03-22

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Variants ─────────────────────────────────────────────────────────────────

/**
 * Visual style of the tab list container.
 *
 * tabs    → underline / bordered style (shadcn default) — highlighted by bottom border on active tab
 * pills   → boxed / filled style — active tab has a filled background pill
 * outline → borderless list; active tab shows a border outline box (Tabs 14 style)
 */
export const tabsListVariants = cva('items-center', {
  variants: {
    variant: {
      tabs: [
        'flex h-auto justify-start',
        'border-b border-border',
        'bg-transparent rounded-none gap-0',
      ],
      pills: ['inline-flex h-9 p-1', 'bg-muted rounded-[var(--radius)]', 'gap-1'],
      outline: ['flex h-auto justify-start', 'bg-muted rounded-[var(--radius)] p-1', 'gap-0.5'],
    },
  },
  defaultVariants: { variant: 'tabs' },
});

/**
 * Visual style of individual tab triggers.
 *
 * tabs    → no background; active tab shows bottom border indicator
 * pills   → active tab receives filled background (bg-background + shadow)
 * outline → active tab shows a border outline box; inactive tabs are borderless plain text
 */
export const tabsTriggerVariants = cva(
  [
    // Base layout
    'inline-flex items-center justify-center whitespace-nowrap',
    'text-sm font-medium',
    'transition-all',
    // Focus ring — uses design-system ring token
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
    // Disabled
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        tabs: [
          'px-4 py-2',
          'rounded-none',
          'border-b-2 border-transparent',
          'text-muted-foreground',
          // Active state — bottom border indicator
          'data-[state=active]:border-primary data-[state=active]:text-foreground',
          // Hover
          'hover:text-foreground',
        ],
        pills: [
          'px-3 h-7',
          'rounded-sm',
          'text-muted-foreground',
          // Active state — filled pill
          'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
          // Hover
          'hover:text-foreground',
        ],
        outline: [
          'px-2.5 py-1',
          'rounded-[calc(var(--radius)-2px)]',
          'border border-transparent',
          'text-[11px] text-muted-foreground',
          // Active state — card background with border outline
          'data-[state=active]:border-border data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm',
          // Hover
          'hover:text-foreground',
        ],
      },
    },
    defaultVariants: { variant: 'tabs' },
  },
);

// ─── Types ────────────────────────────────────────────────────────────────────

export type TabsVariant = 'tabs' | 'pills' | 'outline';

export interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    VariantProps<typeof tabsListVariants> {}

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

export interface TabsContentProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {}

// ─── Context ──────────────────────────────────────────────────────────────────

/**
 * Internal context — passes `variant` from Tabs root down to TabsList and
 * TabsTrigger without requiring consumers to repeat the prop.
 */
const TabsVariantContext = React.createContext<TabsVariant>('tabs');

// ─── Tabs (root) ──────────────────────────────────────────────────────────────

/**
 * Panel switcher with tab list and content areas built on `@radix-ui/react-tabs`.
 *
 * Supports two visual variants:
 * - **tabs** (default) — underline / bordered style. Active tab is indicated
 *   by a bottom border in `--color-primary`.
 * - **pills** — boxed / filled style. Active tab renders as a filled pill on a
 *   muted background container.
 *
 * Keyboard navigation (provided by Radix):
 * - `Left` / `Right` — move focus between triggers (horizontal)
 * - `Up` / `Down` — move focus between triggers (vertical orientation)
 * - `Home` / `End` — jump to first / last trigger
 * - `Space` / `Enter` — activate focused trigger (activationMode="manual")
 *
 * ARIA: `role="tablist"` on list, `role="tab"` on triggers, `role="tabpanel"`
 * on content. `aria-selected`, `aria-controls`, `aria-labelledby` applied
 * automatically by Radix.
 *
 * @example
 * ```tsx
 * <Tabs defaultValue="account">
 *   <TabsList>
 *     <TabsTrigger value="account">Account</TabsTrigger>
 *     <TabsTrigger value="password">Password</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="account">Account settings here.</TabsContent>
 *   <TabsContent value="password">Password settings here.</TabsContent>
 * </Tabs>
 * ```
 *
 * @example Pills variant
 * ```tsx
 * <Tabs defaultValue="overview" variant="pills">
 *   <TabsList>
 *     <TabsTrigger value="overview">Overview</TabsTrigger>
 *     <TabsTrigger value="analytics">Analytics</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="overview">Overview content.</TabsContent>
 *   <TabsContent value="analytics">Analytics content.</TabsContent>
 * </Tabs>
 * ```
 */
const Tabs = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.Root>, TabsProps>(
  ({ className, variant = 'tabs', ...props }, ref) => (
    <TabsVariantContext.Provider value={variant ?? 'tabs'}>
      <TabsPrimitive.Root ref={ref} className={cn('flex flex-col gap-2', className)} {...props} />
    </TabsVariantContext.Provider>
  ),
);
Tabs.displayName = 'Tabs';

// ─── TabsList ─────────────────────────────────────────────────────────────────

/**
 * Container for the tab triggers. Renders as `role="tablist"`.
 *
 * The `variant` prop is inherited from the parent `<Tabs>` via context — you
 * do not need to repeat it here. Pass it explicitly only when using
 * `<TabsList>` standalone (rare).
 */
const TabsList = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant, ...props }, ref) => {
    const contextVariant = React.useContext(TabsVariantContext);
    const resolvedVariant = variant ?? contextVariant;
    return (
      <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ variant: resolvedVariant }), className)}
        {...props}
      />
    );
  },
);
TabsList.displayName = 'TabsList';

// ─── TabsTrigger ──────────────────────────────────────────────────────────────

/**
 * Individual tab button. Renders as `role="tab"`.
 *
 * The `variant` prop is inherited from the parent `<Tabs>` via context.
 */
const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => {
  const contextVariant = React.useContext(TabsVariantContext);
  const resolvedVariant = variant ?? contextVariant;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant: resolvedVariant }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = 'TabsTrigger';

// ─── TabsContent ──────────────────────────────────────────────────────────────

/**
 * Content panel associated with a tab trigger. Renders as `role="tabpanel"`.
 *
 * Receives `aria-labelledby` pointing to its trigger automatically via Radix.
 */
const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 text-sm text-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-[var(--radius)]',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = 'TabsContent';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Tabs, TabsContent, TabsList, TabsTrigger };
